import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { colors, radius, shadow, font } from './tokens'

type ToastVariant = 'info' | 'success' | 'warning' | 'error'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
  duration: number
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  show: (message: string, options?: { variant?: ToastVariant; duration?: number; action?: ToastItem['action'] }) => void
  success: (message: string, opts?: { duration?: number; action?: ToastItem['action'] }) => void
  error: (message: string, opts?: { duration?: number; action?: ToastItem['action'] }) => void
  warning: (message: string, opts?: { duration?: number; action?: ToastItem['action'] }) => void
  info: (message: string, opts?: { duration?: number; action?: ToastItem['action'] }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const variantStyles: Record<ToastVariant, React.CSSProperties> = {
  info: { background: colors.surface, color: colors.textPrimary, borderLeft: `4px solid ${colors.accent}` },
  success: { background: colors.surface, color: colors.textPrimary, borderLeft: `4px solid ${colors.success}` },
  warning: { background: colors.surface, color: colors.textPrimary, borderLeft: `4px solid ${colors.warning}` },
  error: { background: colors.surface, color: colors.textPrimary, borderLeft: `4px solid ${colors.danger}` },
}

const variantIcon: Record<ToastVariant, string> = {
  info: 'ℹ',
  success: '✓',
  warning: '!',
  error: '×',
}

const variantIconColor: Record<ToastVariant, string> = {
  info: colors.accent,
  success: colors.success,
  warning: colors.warning,
  error: colors.danger,
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([])
  const idRef = useRef(1)
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    setItems(prev => prev.filter(i => i.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const show = useCallback<ToastContextValue['show']>((message, options) => {
    const id = idRef.current++
    const variant = options?.variant ?? 'info'
    const duration = options?.duration ?? 4000
    const item: ToastItem = { id, message, variant, duration, action: options?.action }
    setItems(prev => [...prev, item])
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration)
      timersRef.current.set(id, timer)
    }
  }, [dismiss])

  const value: ToastContextValue = {
    show,
    success: (msg, opts) => show(msg, { ...opts, variant: 'success' }),
    error: (msg, opts) => show(msg, { ...opts, variant: 'error', duration: opts?.duration ?? 6000 }),
    warning: (msg, opts) => show(msg, { ...opts, variant: 'warning' }),
    info: (msg, opts) => show(msg, { ...opts, variant: 'info' }),
  }

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach(t => clearTimeout(t))
      timers.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label="通知"
        style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          zIndex: 2000,
          maxWidth: '380px',
          pointerEvents: 'none',
        }}
      >
        {items.map(item => (
          <div
            key={item.id}
            role={item.variant === 'error' || item.variant === 'warning' ? 'alert' : 'status'}
            aria-live={item.variant === 'error' || item.variant === 'warning' ? 'assertive' : 'polite'}
            style={{
              pointerEvents: 'auto',
              padding: '0.75rem 1rem',
              borderRadius: radius.lg,
              boxShadow: shadow.lg,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.625rem',
              fontSize: font.size.base,
              minWidth: '260px',
              ...variantStyles[item.variant],
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: variantIconColor[item.variant],
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.4,
              }}
            >
              {variantIcon[item.variant]}
            </span>
            <div style={{ flex: 1, lineHeight: 1.5 }}>{item.message}</div>
            {item.action && (
              <button
                type="button"
                onClick={() => {
                  item.action!.onClick()
                  dismiss(item.id)
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.accent,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: font.size.sm,
                  padding: 0,
                }}
              >
                {item.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label="通知を閉じる"
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.textSecondary,
                cursor: 'pointer',
                fontSize: '1rem',
                lineHeight: 1,
                padding: '0 0 0 0.25rem',
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}
